/**
 * The owning legal entity of ONE ROW, on a collection surface.
 *
 * The third arm of the "which company?" question, after the app bar's two
 * (`CompanyScopeSwitcher` filters a collection, `PageCompanyIndicator` states a
 * record's own company). Under the **All companies** scope a list interleaves
 * rows from several legal entities, and where document numbers are per-company
 * sequences two of them legitimately read `PO-00001` — so the row itself has to
 * say which entity it belongs to.
 *
 * It renders through {@link CompanyIdentity}, exactly like the two app-bar arms,
 * so the cue is the same object everywhere the question is answered: logo (or
 * monogram) + name + the company's own accent, published as a scoped
 * `--accent-company` per element — which is what a table needs, since every row
 * carries a different company's colour.
 *
 * **Not an `EntityChip`.** A tenant legal entity has no detail route: the
 * `companies` key in the entity URL registry resolves to
 * `/master-data/organizations/{id}`, a different record entirely
 * (`docs/design-system/ui-list-pages.md` § 4.0 → "When NOT to chip"). Six list
 * pages shipped chipping it anyway and linked every row at a stranger's
 * organization page.
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';
import { useActiveCompanyIdOptional } from '../../contexts/ActiveCompanyContext';
import { isCompanyScopedPath } from '../../utils/companyScope';
import { useCompanyScopingMapStore } from '../../utils/companyScopingStore';
import { CompanyIdentity, type CompanyIdentityCompany } from './CompanyIdentity';

export interface RowCompanyIndicatorProps {
  /**
   * The resolved company. `null`/`undefined` renders the neutral "no company"
   * form rather than nothing, so a row whose company is unset (an S? entity) or
   * unresolvable (outside the reader's `accessible_company_ids`) still holds the
   * cell and the column does not go ragged.
   */
  company: CompanyIdentityCompany | null | undefined;
  /**
   * Swatch alone, no name. For the tightest surfaces only — the name still
   * reaches assistive tech through the `sr-only` span below.
   */
  iconOnly?: boolean;
  className?: string;
}

export function RowCompanyIndicator({ company, iconOnly, className }: RowCompanyIndicatorProps) {
  return (
    <span
      className={cn('inline-flex min-w-0 max-w-full items-center', className)}
      title={company?.name}
    >
      <CompanyIdentity company={company} size="sm" iconOnly={iconOnly} />
      {/* `iconOnly` marks the swatch `aria-hidden`, which would leave the cell
          with no accessible name at all. */}
      {iconOnly && company ? <span className="sr-only">{company.name}</span> : null}
    </span>
  );
}

/**
 * ONE visibility gate for the per-row indicator, replacing the two that had
 * drifted apart (accounting gated on `isMultiCompany`, procurement/inventory on
 * `!activeCompanyId`).
 *
 * Three clauses, each answering a different "then it says nothing":
 *
 * 1. **Single-company tenant** — there is no second entity to confuse it with.
 * 2. **Module opted out of the dimension** — a tenant admin can turn company
 *    scoping off per module (WI-4); its rows then have no meaningful owner to
 *    show. The store subscription re-renders when that map loads or changes.
 * 3. **A company is pinned in the app-bar scope** — every row on screen is that
 *    company, so the column is a constant, and a constant column is noise that
 *    costs width the other columns need.
 *
 * The union is deliberate: clause 3 is what the accounting idiom was missing and
 * clause 1 what the procurement/inventory idiom was missing.
 */
export function useRowCompanyVisible(): boolean {
  const { data } = useMyCompanies();
  // The OPTIONAL reader, deliberately. This hook runs inside a row of ~55 lists,
  // and the throwing one turns "no ActiveCompanyProvider above me" into a
  // crashed list rather than the honest answer, which is that no scope is in
  // force. `App.tsx` mounts the provider above every route, so the running app
  // always gets the real value.
  const activeCompanyId = useActiveCompanyIdOptional();
  const { pathname } = useLocation();
  useCompanyScopingMapStore();

  const companyCount = data?.data?.length ?? 0;
  if (companyCount <= 1) return false;
  if (!isCompanyScopedPath(pathname)) return false;
  return !activeCompanyId;
}

/**
 * Resolve a row's `company_id` to the identity the indicator renders.
 *
 * Built ONCE per surface rather than per row: the logo and accent live on the
 * company record, not on the row, so a fifty-row table would otherwise mount
 * fifty query subscriptions to read the same five companies.
 *
 * `fallbackName` covers the row whose company is outside the reader's accessible
 * set — a deep link, or a list a sysadmin is reading across tenants. The name is
 * then all we have: no logo, no accent, but the row still says who owns it,
 * which beats a blank cell.
 */
export function useCompanyLookup(): (
  companyId: string | null | undefined,
  fallbackName?: string | null,
) => CompanyIdentityCompany | null {
  const { data } = useMyCompanies();
  const byId = useMemo(() => {
    const map = new Map<string, CompanyIdentityCompany>();
    for (const company of data?.data ?? []) map.set(company.id, company);
    return map;
  }, [data]);

  return useCallback(
    (companyId, fallbackName) => {
      if (!companyId) return null;
      const known = byId.get(companyId);
      if (known) return known;
      return fallbackName ? { id: companyId, name: fallbackName } : null;
    },
    [byId],
  );
}

/**
 * The whole indicator for a surface that is NOT a `DataTable` — a group header,
 * a card, a hand-rolled `<Table>`, a dialog's row list.
 *
 * `null` when the gate says the indicator is not shown, so the call site reads
 * `{renderCompany?.(row.company_id, row.company_name)}` and carries no gate of
 * its own. A `DataTable` uses {@link useCompanyColumn} instead, which adds the
 * column contract on top of this.
 */
export function useCompanyRowRenderer():
  | ((companyId: string | null | undefined, companyName?: string | null) => ReactNode)
  | null {
  const visible = useRowCompanyVisible();
  const lookup = useCompanyLookup();

  return useMemo(
    () =>
      visible
        ? (companyId: string | null | undefined, companyName?: string | null) => (
            <RowCompanyIndicator company={lookup(companyId, companyName)} />
          )
        : null,
    [visible, lookup],
  );
}
